import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
    User,
    MapPin,
    Award,
    Clock,
    Star,
    MessageCircle,
    Instagram,
    Globe,
    CheckCircle,
    Dumbbell,
    Users,
    Calendar,
    Phone,
    Loader2,
} from 'lucide-react';

interface PublicBioData {
    id: string;
    user_id: string;
    display_name: string;
    headline: string;
    bio: string;
    workout_styles: string[];
    experience_years: number;
    price_per_session: number;
    price_monthly: number;
    location_city: string;
    location_state: string;
    accepts_online: boolean;
    accepts_in_person: boolean;
    instagram_url: string;
    whatsapp_number: string;
    website_url: string;
    certifications: string[];
    photos: string[];
    is_visible: boolean;
}

export function PublicBioPage() {
    const { slug } = useParams<{ slug: string }>();

    const { data: tenant, isLoading: loadingTenant } = useQuery({
        queryKey: ['public-tenant', slug],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('tenants')
                .select('id, name, slug, owner_id, logo_url, bio_page_enabled, bio_page_slug, settings')
                .eq('bio_page_slug', slug)
                .eq('bio_page_enabled', true)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !!slug,
    });

    const { data: profile, isLoading: loadingProfile } = useQuery({
        queryKey: ['public-bio', tenant?.owner_id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('tinderfit_profiles')
                .select('*')
                .eq('user_id', tenant?.owner_id)
                .eq('is_visible', true)
                .single();

            if (error && error.code !== 'PGRST116') throw error;
            return data as PublicBioData | null;
        },
        enabled: !!tenant?.owner_id,
    });

    const { data: owner } = useQuery({
        queryKey: ['public-owner', tenant?.owner_id],
        queryFn: async () => {
            const { data } = await supabase
                .from('users')
                .select('full_name, avatar_url, phone')
                .eq('id', tenant?.owner_id)
                .single();
            return data;
        },
        enabled: !!tenant?.owner_id,
    });

    const isLoading = loadingTenant || loadingProfile;

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-primary-950 to-gray-900 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
            </div>
        );
    }

    if (!tenant || !tenant.bio_page_enabled) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-primary-950 to-gray-900 flex items-center justify-center text-center px-4">
                <div>
                    <Dumbbell className="w-16 h-16 text-primary-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Página não encontrada</h1>
                    <p className="text-gray-400">Este perfil não está disponível ou foi desativado.</p>
                </div>
            </div>
        );
    }

    const photoUrl = profile?.photos?.[0] || owner?.avatar_url;
    const displayName = profile?.display_name || owner?.full_name || tenant.name;
    const whatsappNumber = profile?.whatsapp_number || owner?.phone || '';
    const whatsappLink = whatsappNumber ? `https://wa.me/55${whatsappNumber.replace(/\D/g, '')}?text=Olá! Vi seu perfil no GoFit e gostaria de saber mais sobre seus serviços.` : '';

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-primary-950 to-gray-900">
            {/* Hero Section */}
            <div className="relative pt-12 pb-32 px-4">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-2xl mx-auto text-center">
                    {/* Avatar */}
                    <div className="mb-6">
                        <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-primary-400 to-primary-600 p-1 shadow-xl shadow-primary-500/25">
                            <div className="w-full h-full rounded-full overflow-hidden bg-gray-800">
                                {photoUrl ? (
                                    <img src={photoUrl} alt={displayName} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <User className="w-16 h-16 text-gray-400" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Name & Headline */}
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                        {displayName}
                    </h1>
                    {profile?.headline && (
                        <p className="text-lg text-primary-300 font-medium mb-4">
                            {profile.headline}
                        </p>
                    )}

                    {/* Location & Experience */}
                    <div className="flex items-center justify-center gap-4 text-gray-400 text-sm mb-6">
                        {profile?.location_city && (
                            <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {profile.location_city}{profile.location_state ? `, ${profile.location_state}` : ''}
                            </span>
                        )}
                        {profile?.experience_years && profile.experience_years > 0 && (
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {profile.experience_years} anos de experiência
                            </span>
                        )}
                    </div>

                    {/* CTA Button */}
                    {whatsappLink && (
                        <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full shadow-lg shadow-green-500/25 transition-all hover:scale-105"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Entrar em Contato
                        </a>
                    )}
                </div>
            </div>

            {/* Content Section */}
            <div className="relative -mt-16 pb-16 px-4">
                <div className="max-w-2xl mx-auto space-y-6">
                    {/* Bio Card */}
                    {profile?.bio && (
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
                            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                <User className="w-5 h-5 text-primary-400" />
                                Sobre Mim
                            </h2>
                            <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                                {profile.bio}
                            </p>
                        </div>
                    )}

                    {/* Specialties */}
                    {profile?.workout_styles && profile.workout_styles.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Award className="w-5 h-5 text-primary-400" />
                                Especialidades
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {profile.workout_styles.map((style) => (
                                    <span
                                        key={style}
                                        className="px-4 py-2 bg-primary-500/20 text-primary-300 rounded-full text-sm font-medium border border-primary-500/30"
                                    >
                                        {style}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Services & Pricing */}
                    <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
                        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <Dumbbell className="w-5 h-5 text-primary-400" />
                            Serviços
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {profile?.price_per_session && profile.price_per_session > 0 && (
                                <div className="bg-white/5 rounded-xl p-4 text-center">
                                    <p className="text-3xl font-bold text-white">
                                        R$ {profile.price_per_session.toFixed(0)}
                                    </p>
                                    <p className="text-gray-400 text-sm">por sessão</p>
                                </div>
                            )}
                            {profile?.price_monthly && profile.price_monthly > 0 && (
                                <div className="bg-primary-500/20 rounded-xl p-4 text-center border border-primary-500/30">
                                    <p className="text-3xl font-bold text-primary-300">
                                        R$ {profile.price_monthly.toFixed(0)}
                                    </p>
                                    <p className="text-primary-400 text-sm">mensal</p>
                                    <span className="inline-block mt-2 text-xs bg-primary-500 text-white px-2 py-1 rounded-full">
                                        Melhor custo-benefício
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Modalities */}
                        <div className="flex flex-wrap gap-3 mt-4">
                            {profile?.accepts_online && (
                                <span className="flex items-center gap-2 text-green-400 text-sm">
                                    <CheckCircle className="w-4 h-4" />
                                    Atendimento Online
                                </span>
                            )}
                            {profile?.accepts_in_person && (
                                <span className="flex items-center gap-2 text-green-400 text-sm">
                                    <CheckCircle className="w-4 h-4" />
                                    Atendimento Presencial
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Certifications */}
                    {profile?.certifications && profile.certifications.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Star className="w-5 h-5 text-primary-400" />
                                Certificações
                            </h2>
                            <ul className="space-y-2">
                                {profile.certifications.map((cert, index) => (
                                    <li key={index} className="flex items-center gap-2 text-gray-300">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                        {cert}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Social Links */}
                    <div className="flex items-center justify-center gap-4">
                        {profile?.instagram_url && (
                            <a
                                href={profile.instagram_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg"
                            >
                                <Instagram className="w-6 h-6" />
                            </a>
                        )}
                        {whatsappLink && (
                            <a
                                href={whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg"
                            >
                                <Phone className="w-6 h-6" />
                            </a>
                        )}
                        {profile?.website_url && (
                            <a
                                href={profile.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg"
                            >
                                <Globe className="w-6 h-6" />
                            </a>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="text-center pt-8 border-t border-white/10">
                        <p className="text-gray-500 text-sm">
                            Perfil criado com
                        </p>
                        <a href="/" className="inline-flex items-center gap-2 text-primary-400 font-semibold hover:text-primary-300 transition-colors">
                            <img src="/gofit-logo.png" alt="GoFit" className="w-5 h-5" />
                            GoFit
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
